const HEIC_MIME_TYPES = new Set([
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence'
]);

const JPEG_QUALITY = 0.92;

let decoderPromise = null;

const isHeicFile = (file) => {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return HEIC_MIME_TYPES.has(type) || /\.(heic|heif)$/.test(name);
};

const toJpegName = (fileName) => {
  const replaced = String(fileName || 'image').replace(
    /\.(heic|heif)$/i,
    '.jpg'
  );
  return /\.jpe?g$/i.test(replaced) ? replaced : `${replaced}.jpg`;
};

const jpegFileFromBlob = (file, blob) =>
  new File([blob], toJpegName(file.name), {
    type: 'image/jpeg',
    lastModified: file.lastModified || Date.now()
  });

const isJpegBytes = (bytes) =>
  bytes.length >= 3 &&
  bytes[0] === 0xff &&
  bytes[1] === 0xd8 &&
  bytes[2] === 0xff;

const canvasToJpegBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Could not encode JPEG'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      JPEG_QUALITY
    );
  });

const convertWithBrowser = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    const cleanup = () => URL.revokeObjectURL(url);

    image.onload = async () => {
      try {
        const width = image.naturalWidth;
        const height = image.naturalHeight;
        if (!width || !height) {
          throw new Error('HEIC image has invalid dimensions');
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('Could not create image canvas');
        }

        context.drawImage(image, 0, 0);
        resolve(jpegFileFromBlob(file, await canvasToJpegBlob(canvas)));
      } catch (error) {
        reject(error);
      } finally {
        cleanup();
      }
    };

    image.onerror = () => {
      cleanup();
      reject(new Error('browser cannot decode HEIC'));
    };

    image.src = url;
  });

const getDecoder = () => {
  if (!decoderPromise) {
    decoderPromise = import('libheif-js/wasm-bundle')
      .then((mod) => {
        const libheif = mod?.HeifDecoder ? mod : mod?.default;
        if (!libheif?.HeifDecoder) {
          throw new Error('HEIC decoder failed to load');
        }
        return new libheif.HeifDecoder();
      })
      .catch((error) => {
        decoderPromise = null;
        throw error;
      });
  }

  return decoderPromise;
};

const displayOnCanvas = (image) =>
  new Promise((resolve, reject) => {
    const width = image.get_width();
    const height = image.get_height();

    if (!width || !height) {
      reject(new Error('HEIC image has invalid dimensions'));
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');

    if (!context) {
      reject(new Error('Could not create image canvas'));
      return;
    }

    const imageData = context.createImageData(width, height);
    image.display(imageData, (displayData) => {
      if (!displayData) {
        reject(new Error('HEIC processing error'));
        return;
      }

      context.putImageData(imageData, 0, 0);
      resolve(canvas);
    });
  });

const convertWithLibheif = async (file) => {
  const decoder = await getDecoder();
  const bytes = new Uint8Array(await file.arrayBuffer());
  const images = decoder.decode(bytes);

  if (!images?.length) {
    throw new Error('No image found in HEIC file');
  }

  const image = images.find((item) => item.is_primary()) || images[0];

  try {
    const canvas = await displayOnCanvas(image);
    return jpegFileFromBlob(file, await canvasToJpegBlob(canvas));
  } finally {
    images.forEach((item) => item.free());
  }
};

export const convertHeicToJpeg = async (file) => {
  if (!isHeicFile(file)) return file;

  const header = new Uint8Array(await file.slice(0, 3).arrayBuffer());
  if (isJpegBytes(header)) {
    return jpegFileFromBlob(file, file);
  }

  try {
    return await convertWithBrowser(file);
  } catch (browserError) {
    try {
      return await convertWithLibheif(file);
    } catch (libheifError) {
      const detail =
        libheifError?.message || browserError?.message || 'unknown error';
      throw new Error(`Could not convert ${file.name} to JPEG (${detail})`);
    }
  }
};
